/**
 * Shared data model types for Bifrost RPC communication.
 *
 * These types mirror Kotlin @Serializable data classes in the
 * `com.seekrtech.stforestkit.web.bifrost.types` package, providing
 * type-safe contracts for common payloads exchanged over the bridge.
 */

/**
 * A reusable image type for Bifrost RPC communication.
 * Uses base64-encoded string for transport over the JSON/WebView bridge.
 *
 * @example
 * ```typescript
 * // Receiving from native:
 * const image: BifrostImage = await bifrost.call('MyService', 'getAvatar');
 * img.src = bifrostImageToDataUrl(image);
 *
 * // Sending to native:
 * const image: BifrostImage = { base64: '...', mimeType: 'image/png', width: 128, height: 128 };
 * await bifrost.call('MyService', 'uploadAvatar', image);
 * ```
 */
export interface BifrostImage {
  /** Base64-encoded image data (no data URL prefix) */
  base64: string;
  /** MIME type (e.g., "image/png", "image/jpeg", "image/webp") */
  mimeType: string;
  /** Image width in pixels (0 if unknown) */
  width?: number;
  /** Image height in pixels (0 if unknown) */
  height?: number;
  /** Raw image size in bytes before base64 encoding (0 if unknown) */
  sizeBytes?: number;
}

/**
 * Convert a BifrostImage to a data URL for use in `<img src="...">`.
 */
export function bifrostImageToDataUrl(image: BifrostImage): string {
  return `data:${image.mimeType};base64,${image.base64}`;
}
